import React from 'react';

import { getBillingDashboardPageBreadcrumb } from '../Billing/BillingDashboardPage';

import DataTable from '../../Fields/DataTable';

import PageHeader from '../../Common/PageHeader';
import NavigationBreadcrumbs from '../../Common/NavigationBreadcrumbs';
import ProcessingIndicator from '../../Common/ProcessingIndicator';

import BillingProfile from '../../../Entities/BillingProfile';

import { queryListBillingProfiles } from '../../../Queries';

import { getFacility } from '../../../Utils/facility';

const BillingProfileQueryFields = [
  { 'billing_profiles': [
    'id',
    'name',
    'active',
    'billing_day',
    'billing_timing',
    'payment_due_day',
    'decline_recurring'
  ] }
];

const BillingProfileListDataTableColumns = [
  { id: 'name', heading: 'Name' },
  { id: 'id', heading: 'ID' },
  { id: 'active', heading: 'Status' },
  { id: 'billing_day', heading: 'Billing Day' },
  { id: 'payment_due_day', heading: 'Payment Due Day' },
  { id: 'decline_recurring', heading: 'Decline Recurring' }
];

class BillingProfileListPage extends React.Component {
  state = {
    pageInitializationComplete: false,
    pageInitializationErrors: [],
    currentlyRetrievingBillingProfiles: false,
    billingProfileQueryErrors: [],
    billingProfileListDataTableRows: []
  };

  facilityId = null;
  facility = null;

  fieldConfiguration = {};


  constructor(props) {
    super(props);

    const urlSearchParams = new URLSearchParams(window.location.search);
    const facilityId = urlSearchParams.get('facility');
    if (facilityId !== null) this.facilityId = facilityId;
  }


  componentDidMount() {
    const initializationTasks = [];

    if (this.facilityId !== null) {
      initializationTasks.push(this.fetchFacility(this.facilityId));
    }

    initializationTasks.push(this.fetchFieldDisplayValues());

    Promise.all(initializationTasks).then(() => {
      this.setState({ pageInitializationComplete: true }, () => {
        this.fetchBillingProfiles();
      });
    }, errors => {
      this.setState({
        pageInitializationComplete: true,
        pageInitializationErrors: errors.map(({ message }) => message)
      });
    });
  }


  async fetchFacility(facilityId) {
    try {
      this.facility = await getFacility(facilityId);
    } catch (errors) {
      throw Array.isArray(errors) ? errors : [errors];
    }
  }


  fetchBillingProfiles() {
    this.setState({ currentlyRetrievingBillingProfiles: true }, () => {
      const options = {};

      if (this.facilityId !== null) {
        options.filters = [{
          model: 'BILLING_PROFILE',
          field: 'facility_id',
          op: 'EQ',
          value: this.facilityId
        }];
      }

      queryListBillingProfiles(options, BillingProfileQueryFields).then(response => {
        if (response.errors) {
          this.setState({
            currentlyRetrievingBillingProfiles: false,
            billingProfileQueryErrors: response.errors.map(error => error.message)
          });
          return;
        }

        const billingProfiles = response.data.listBillingProfiles.billing_profiles.map(billingProfile => {
          return BillingProfile.createFromQueryValues(billingProfile);
        });
        const billingProfileListDataTableRows = this.getBillingProfileListDataTableRows(billingProfiles);

        this.setState({
          currentlyRetrievingBillingProfiles: false,
          billingProfileListDataTableRows
        });
      }, error => {
        this.setState({
          currentlyRetrievingBillingProfiles: false,
          billingProfileQueryErrors: [error]
        });
      });
    });
  }


  fetchFieldDisplayValues() {
    return new Promise((resolve, reject) => {
      BillingProfile.getFieldAllowedValues([
        'active',
        'billing_day',
        'payment_due_day',
        'decline_recurring'
      ]).then(response => {
        if (response.errors) {
          reject(response.errors);
          return;
        }

        this.fieldConfiguration = {
          ...this.fieldConfiguration,
          ...response
        };
        resolve();
      }, error => {
        reject([error]);
      });
    });
  }


  getBillingProfileListDataTableRows(billingProfiles = []) {
    return billingProfiles.map(billingProfile => {
      const row = [];
      for (let column of BillingProfileListDataTableColumns) {
        let fieldValueOption;
        switch (column.id) {
          case 'name':
            row.push(`<a href="?page=parkchirp_billing_profile&billingProfile=${billingProfile.fields.id.value}${(this.facilityId !== null) ? `&facility=${this.facilityId}` : ''}">${billingProfile.fields.name.value}</a>`);
            break;
          case 'id':
            row.push(billingProfile.fields[column.id].value);
            break;
          case 'active':
          case 'decline_recurring':
            fieldValueOption = this.fieldConfiguration[column.id].options.find(e => e.value === billingProfile.fields[column.id].value);
            row.push((fieldValueOption) ? fieldValueOption.label : billingProfile.fields[column.id].value);
            break;
          case 'payment_due_day':
            fieldValueOption = this.fieldConfiguration[column.id].options.find(e => e.value === billingProfile.fields[column.id].value);
            row.push((fieldValueOption) ? `${fieldValueOption.label} day of the month` : billingProfile.fields[column.id].value);
            break;
          case 'billing_day':
            switch (billingProfile.fields.billing_timing.value) {
              case 'CALENDAR_DAY':
                fieldValueOption = this.fieldConfiguration[column.id].options[billingProfile.fields.billing_timing.value].find(e => e.value === billingProfile.fields[column.id].value)
                row.push((fieldValueOption) ? `${fieldValueOption.label} day of the month` : billingProfile.fields[column.id].value);
                break;
              case 'ROLLING_PERIOD':
                fieldValueOption = this.fieldConfiguration[column.id].options[billingProfile.fields.billing_timing.value].find(e => e.value === billingProfile.fields[column.id].value)
                row.push((fieldValueOption) ? `${fieldValueOption.label} day(s) prior to start of new billing period` : billingProfile.fields[column.id].value);
                break;
              default:
                row.push(billingProfile.fields[column.id].value);
            }
            break;
          default:
        }
      }
      return row;
    });
  }


  renderNavigationBreadcrumbs() {
    const breadcrumbs = [];
    breadcrumbs.push(getBillingDashboardPageBreadcrumb(false, this.facilityId));
    breadcrumbs.push(getBillingProfileListPageBreadcrumb());

    return (
      <NavigationBreadcrumbs breadcrumbs={breadcrumbs} />
    );
  }


  renderPageHeader() {
    const facilityLinkParam = (this.facilityId !== null) ? `&facility=${this.facilityId}` : '';

    const heading = 'Billing Profiles';
    const subheading = (this.facility === null) ? null :
      this.facility.fields.facility_name.value;
    const actions = [
      <a href={`?page=parkchirp_billing_profile${facilityLinkParam}`}
        className="button button-primary page-action">
        Create New Billing Profile
      </a>
    ];

    return (
      <PageHeader heading={heading}
        subheading={subheading}
        actions={actions} />
    );
  }


  renderPageInitializationProcessing() {
    return (
      <div className="parkchirp--page--user-message-container">
        <ProcessingIndicator userMessage="Initializing, please wait..." />
      </div>
    );
  }


  renderPageInitializationError() {
    const {
      pageInitializationErrors
    } = this.state;

    return (
      <div className="parkchirp--page--user-message-container">
        <div className="parkchirp--page--user-message">
          <p>The following errors occurred while initializing the page:</p>
          <ul>
            {pageInitializationErrors.map(error => <li key={performance.now()}>{error}</li>)}
          </ul>
        </div>
      </div>
    );
  }


  renderBillingProfileListDataTable() {
    const {
      currentlyRetrievingBillingProfiles,
      billingProfileQueryErrors,
      billingProfileListDataTableRows
    } = this.state;

    return (
      <React.Fragment>
        {(currentlyRetrievingBillingProfiles) &&
          <div className="parkchirp--page--user-message-container">
            <ProcessingIndicator userMessage="Retrieving data, please wait..." />
          </div>
        }
        {(!currentlyRetrievingBillingProfiles && billingProfileQueryErrors.length > 0) &&
          <div className="parkchirp--page--user-message-container">
            <div className="parkchirp--page--user-message">
              <p>The following errors occurred while retrieving the Billing Profile data:</p>
              <ul>
                {billingProfileQueryErrors.map(error => <li key={performance.now()}>{error}</li>)}
              </ul>
            </div>
          </div>
        }
        {(!currentlyRetrievingBillingProfiles && billingProfileQueryErrors.length === 0) &&
          <DataTable id="billing-profile-list"
            columns={BillingProfileListDataTableColumns}
            rows={billingProfileListDataTableRows}
            overallRowCount={billingProfileListDataTableRows.length}
          />
        }
      </React.Fragment>
    );
  }


  render() {
    const {
      pageInitializationComplete,
      pageInitializationErrors
    } = this.state;

    return (
      <div className="parkchirp--page parkchirp--page--billing-profile-list">
        <div className="parkchirp--page-content">
          {this.renderNavigationBreadcrumbs()}
          {this.renderPageHeader()}
          {(!pageInitializationComplete) &&
            this.renderPageInitializationProcessing()
          }
          {(pageInitializationErrors.length > 0) &&
            this.renderPageInitializationError()
          }
          {(pageInitializationComplete && pageInitializationErrors.length === 0) &&
            this.renderBillingProfileListDataTable()
          }
        </div>
      </div>
    );
  }
}
export default BillingProfileListPage;


export function getBillingProfileListPageBreadcrumb(currentPage = true, facilityId = null) {
  const title = 'Billing Profiles';
  const href = (currentPage) ? null :
    `?page=parkchirp_billing_profiles${(facilityId !== null) ? `&facility=${facilityId}` : ''}`;

  return { title, href };
}
